import { useMemo } from 'react';

import { colors } from '@ui';
import * as THREE from 'three';

import { getKeypointId, getSafeSkeleton, getMeasurementValuesByName } from '../helpers';

import type { FrameFirstSkeleton } from '../skeletonUtils';
import type { KeypointIdToName } from '../types';
import type { IMeasurement } from '@core';

type SpineMeshProps = {
    frame: number;
    skeleton: FrameFirstSkeleton;
    keypointIdToName: KeypointIdToName;
    measurements?: IMeasurement[];
    isTranslucent?: boolean;
    isComparison?: boolean;
};

const SPINE_RADIUS = 0.03;
const BONE_SEGMENTS = 32;
const CONTROL_POINT_SCALE = 0.1;

// Size of bone connectors as fraction of total curve length
const BONE_SIZE = 0.25;
// Center bone is smaller than the end bones
const CENTER_BONE_SCALE = 0.6;

function getJointForwardDirection(
    measurements: IMeasurement[] | undefined,
    measurementName: string,
    frame: number,
): THREE.Vector3 | null {
    const angularPosition = getMeasurementValuesByName(measurements, measurementName, frame);
    if (!angularPosition || angularPosition.length < 3) return null;

    const [bend, tilt, rotation] = angularPosition;
    const quaternion = new THREE.Quaternion().setFromEuler(new THREE.Euler(bend, tilt, rotation, 'XYZ'));

    return new THREE.Vector3(1, 0, 0).applyQuaternion(quaternion);
}

function getSkeletonPoint(
    skeleton: FrameFirstSkeleton,
    frame: number,
    keypointName: string,
    keypointIdToName: KeypointIdToName,
): THREE.Vector3 | null {
    try {
        const id = getKeypointId(keypointName, keypointIdToName);
        const pos = getSafeSkeleton(skeleton, frame, id);
        return pos ? new THREE.Vector3(...pos) : null;
    } catch {
        return null;
    }
}

type SpineBone = { start: THREE.Vector3; end: THREE.Vector3; radiusScale?: number };

// Compute 5 bones along the bezier curve
function useSpineBones(
    skeleton: FrameFirstSkeleton,
    frame: number,
    keypointIdToName: KeypointIdToName,
    measurements?: IMeasurement[],
): SpineBone[] | null {
    return useMemo(() => {
        const pelvis = getSkeletonPoint(skeleton, frame, 'pelvis', keypointIdToName);
        const upperTorso = getSkeletonPoint(skeleton, frame, 'upper_torso', keypointIdToName);
        if (!pelvis || !upperTorso) return null;

        const pelvisForward = getJointForwardDirection(measurements, 'pelvis_angular_position', frame);
        const torsoForward = getJointForwardDirection(measurements, 'upper_torso_angular_position', frame);

        const spineLength = pelvis.distanceTo(upperTorso);

        const p0 = pelvis;
        const p3 = upperTorso;

        let p1: THREE.Vector3;
        let p2: THREE.Vector3;

        if (pelvisForward && torsoForward) {
            p1 = p0.clone().addScaledVector(pelvisForward.negate(), spineLength * CONTROL_POINT_SCALE);
            p2 = p3.clone().addScaledVector(torsoForward, -spineLength * CONTROL_POINT_SCALE);
        } else {
            p1 = p0.clone().lerp(p3, 1 / 3);
            p2 = p0.clone().lerp(p3, 2 / 3);
        }

        const fullCurve = new THREE.CubicBezierCurve3(p0, p1, p2, p3);

        // 5 bones along the curve: start, lower-mid, center, upper-mid, end
        const centerBoneSize = BONE_SIZE * CENTER_BONE_SCALE;
        const halfCenterBone = centerBoneSize / 2;

        const endBoneRadius = 0.9;

        return [
            // Start bone (pelvis) - thinner
            { start: fullCurve.getPoint(0), end: fullCurve.getPoint(BONE_SIZE), radiusScale: endBoneRadius },
            // Lower-mid bone (between start and center)
            { start: fullCurve.getPoint(BONE_SIZE), end: fullCurve.getPoint(0.5 - halfCenterBone) },
            // Center bone (pivot) - smaller
            { start: fullCurve.getPoint(0.5 - halfCenterBone), end: fullCurve.getPoint(0.5 + halfCenterBone) },
            // Upper-mid bone (between center and end)
            { start: fullCurve.getPoint(0.5 + halfCenterBone), end: fullCurve.getPoint(1 - BONE_SIZE) },
            // End bone (upper torso) - thinner
            { start: fullCurve.getPoint(1 - BONE_SIZE), end: fullCurve.getPoint(1), radiusScale: endBoneRadius },
        ];
    }, [skeleton, frame, keypointIdToName, measurements]);
}

type SpineBoneConnectorProps = {
    start: THREE.Vector3;
    end: THREE.Vector3;
    radiusScale?: number;
    isTranslucent?: boolean;
    isComparison?: boolean;
};

function SpineBoneConnector({ start, end, radiusScale = 1, isTranslucent, isComparison }: SpineBoneConnectorProps) {
    const { center, quaternion, mainLength, tipLength, radius } = useMemo(() => {
        const direction = end.clone().sub(start);
        const length = direction.length();
        const centerPos = start.clone().add(end).multiplyScalar(0.5);

        // Align Y-axis with bone direction
        const yAxis = direction.normalize().negate();
        const xAxis = new THREE.Vector3().crossVectors(yAxis, new THREE.Vector3(1, 0, 0)).normalize();
        const zAxis = new THREE.Vector3().crossVectors(xAxis, yAxis).normalize();
        const rotMatrix = new THREE.Matrix4().makeBasis(xAxis, yAxis, zAxis);
        const quat = new THREE.Quaternion().setFromRotationMatrix(rotMatrix);

        // Bone proportions: shorter tip so it doesn't protrude
        const tip = length * 0.2;
        const main = length * 0.8;

        return {
            center: centerPos,
            quaternion: quat,
            mainLength: main,
            tipLength: tip,
            radius: SPINE_RADIUS * radiusScale,
        };
    }, [start, end, radiusScale]);

    const opacity = isComparison ? 0.5 : isTranslucent ? 0.25 : 1;
    const color = isComparison ? colors.slate[400] : isTranslucent ? colors.slate[300] : colors.slate[50];
    const GIRTH = 2 / 3;

    return (
        <group position={center} quaternion={quaternion}>
            <mesh position-y={(mainLength + tipLength) / 2}>
                <cylinderGeometry args={[0, radius * GIRTH, tipLength, BONE_SEGMENTS, 1, true]} />
                <meshStandardMaterial color={color} flatShading opacity={opacity} transparent />
            </mesh>

            <mesh>
                <cylinderGeometry args={[radius * GIRTH, radius, mainLength, BONE_SEGMENTS, 1, true]} />
                <meshStandardMaterial color={color} flatShading opacity={opacity} transparent />
            </mesh>

            <mesh position-y={-(mainLength + tipLength) / 2}>
                <cylinderGeometry args={[radius, 0, tipLength, BONE_SEGMENTS, 1, true]} />
                <meshStandardMaterial color={color} flatShading opacity={opacity} transparent />
            </mesh>
        </group>
    );
}

// Spine rendered as 5 bones along a Bezier curve
export const SpineMesh = ({
    frame,
    skeleton,
    keypointIdToName,
    measurements,
    isTranslucent = false,
    isComparison = false,
}: SpineMeshProps) => {
    const spineBones = useSpineBones(skeleton, frame, keypointIdToName, measurements);

    if (!spineBones) return null;

    return (
        <group>
            {spineBones.map((bone, idx) => (
                <SpineBoneConnector
                    key={`spine-bone-${idx}`}
                    start={bone.start}
                    end={bone.end}
                    radiusScale={bone.radiusScale}
                    isTranslucent={isTranslucent}
                    isComparison={isComparison}
                />
            ))}
        </group>
    );
};
